import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'
import type { User, ApiResponse } from '@/types'

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
}

export function useUsers() {
  return useQuery({
    queryKey: userKeys.lists(),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<User[]>>('/api/users')
      return data
    },
  })
}

interface InviteUserInput {
  email: string
  first_name: string
  last_name: string
  role: 'staff' | 'admin'
}

export function useInviteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: InviteUserInput) => {
      const { data } = await api.post<ApiResponse<User>>('/api/users', { user: input })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, userKeys.lists())
    },
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, role }: { id: number; role: string }) => {
      const { data } = await api.patch<ApiResponse<User>>(`/api/users/${id}`, {
        user: { role },
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, userKeys.lists())
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/users/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, userKeys.lists())
    },
  })
}

export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.post(`/api/users/${id}/resend_invitation`),
    onSuccess: () => {
      removeListQueries(queryClient, userKeys.lists())
    },
  })
}
